import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { isStripeEnabled } from "common";
/* eslint-disable-next-line import/no-unassigned-import */
import "common/src/helpers/validation";
import React, { ReactNode, useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { queryClient } from "../api/queryClient";
import { config } from "../config";
/* eslint-disable-next-line import/no-unassigned-import */
import "../dayjs";
import { useSeller } from "../hooks/useAccount";
import { ErrorBoundary } from "./ErrorBoundary";
import { XolaMobileAppsProvider } from "./MobileApps/XolaMobileAppsContext";
import { Router } from "./Router";
import { UiKitProvider } from "./UiKitProvider";
import { x2CheckoutUri } from "@/api/checkoutUri";
import { UnassignGuidePromptHost } from "@/components/Dashboard/Resources/Guides/UnassignGuidePromptHost";

export const App = () => {
    return (
        <ErrorBoundary>
            <HelmetProvider>
                {/* Pre-connect to X2 checkout URI to speed up DNS look up and load the preview faster */}
                <link href={x2CheckoutUri} rel="preconnect" />
                <XolaMobileAppsProvider>
                    <QueryClientProvider client={queryClient}>
                        <Toaster position="top-right" />
                        <StripeElements>
                            <UiKitProvider>
                                <Router />
                                <UnassignGuidePromptHost isFallback />
                            </UiKitProvider>
                        </StripeElements>
                        {import.meta.env.PROD ? null : <ReactQueryDevtools position="bottom-right" />}
                    </QueryClientProvider>
                </XolaMobileAppsProvider>
            </HelmetProvider>
        </ErrorBoundary>
    );
};

interface StripeElementsProps {
    children: ReactNode;
}

export const StripeElements = ({ children }: StripeElementsProps) => {
    const seller = useSeller();
    const [stripePublicKey, setStripePublicKey] = useState(config.stripe.publicKey);
    const [stripePromise, setStripePromise] = useState(stripePublicKey ? loadStripe(stripePublicKey) : null);

    useEffect(() => {
        if (seller?.defaultGateway?.publicKey && isStripeEnabled(seller)) {
            setStripePublicKey(seller.defaultGateway.publicKey);
            setStripePromise(loadStripe(seller.defaultGateway.publicKey));
        }
    }, [seller]);

    return (
        <Elements key={stripePublicKey} stripe={stripePromise}>
            {children}
        </Elements>
    );
};
